import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { tournamentsAPI, publicAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, cn } from '../lib/utils';
import {
  Trophy, Users, Plus, Trash2, Play, CheckCircle, Share2, Copy,
  Swords, Crown, ArrowRight, ChevronRight, Clock, MapPin, Phone, User,
  Shuffle, Pencil, ArrowLeftRight, Mail, Radio, Lock, Eye, EyeOff, RotateCw, Sparkles, Flag,
} from 'lucide-react';
import TournamentMatchScorer from '../components/TournamentMatchScorer';
import BracketTreeView from '../components/BracketTreeView';
import ScoringPinDialog from '../components/ScoringPinDialog';
import ChampionBragCard from '../components/tournament/ChampionBragCard';
import ExpandBracketBanner from '../components/tournament/ExpandBracketBanner';
import ScoringAccessCard from '../components/tournament/ScoringAccessCard';

const chr = (code) => String.fromCharCode(code);

export default function TournamentDetail() {
  const { tournamentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [pointsTable, setPointsTable] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [knockoutView, setKnockoutView] = useState('bracket'); // 'bracket' | 'list'
  const [inviting, setInviting] = useState(false);

  // — Scoring PIN —
  const [scoringPin, setScoringPin] = useState(null); // { pin, rotates_at, timezone }
  const [showPin, setShowPin] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pendingScoringAction, setPendingScoringAction] = useState(null); // () => Promise<void>

  // — Bracket expansion —
  const [expandDialogOpen, setExpandDialogOpen] = useState(false);
  const [expandStrategy, setExpandStrategy] = useState('append');
  const [expanding, setExpanding] = useState(false);

  // Team form
  const [addTeamOpen, setAddTeamOpen] = useState(false);
  const [teamForm, setTeamForm] = useState({
    player1_name: '', player2_name: '', team_name: '',
    email: '', partner_email: '', mobile: '', partner_mobile: '',
    member_id: '', category: 'doubles',
  });

  // Umpires
  const [umpires, setUmpires] = useState([]);
  const [addUmpireOpen, setAddUmpireOpen] = useState(false);
  const [umpireForm, setUmpireForm] = useState({ name: '', email: '', mobile: '' });

  // Start match dialog
  const [startMatchOpen, setStartMatchOpen] = useState(false);
  const [matchToStart, setMatchToStart] = useState(null);
  const [selectedUmpireId, setSelectedUmpireId] = useState('');

  // Pool creation
  const [numPools, setNumPools] = useState('2');
  const [creatingPools, setCreatingPools] = useState(false);
  const [poolPreview, setPoolPreview] = useState(null); // {A: [team_id,...], B: [...]}

  // Edit team
  const [editTeamOpen, setEditTeamOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editForm, setEditForm] = useState({ team_name: '', player1_name: '', player2_name: '' });

  // Edit team count (max_participants)
  const [editCountOpen, setEditCountOpen] = useState(false);
  const [editCountValue, setEditCountValue] = useState('');
  const [savingCount, setSavingCount] = useState(false);

  // Scoring dialog
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [scoringMatch, setScoringMatch] = useState(null);
  const [gameScores, setGameScores] = useState([{ team1_score: 0, team2_score: 0 }]);
  const [selectedWinner, setSelectedWinner] = useState('');

  // Walkover
  const [walkoverMatch, setWalkoverMatch] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [tRes, mRes, uRes] = await Promise.all([
        tournamentsAPI.getById(tournamentId),
        // Try authenticated matches endpoint; fall back to the public one for
        // anonymous spectators viewing a share link.
        tournamentsAPI.getMatches(tournamentId)
          .catch(() => publicAPI.getTournamentMatches(tournamentId))
          .catch(() => ({ data: [] })),
        tournamentsAPI.listUmpires(tournamentId).catch(() => ({ data: [] })),
      ]);
      setTournament(tRes.data);
      setMatches(mRes.data || []);
      setUmpires(uRes.data || []);

      if (tRes.data?.status === 'in_progress' || tRes.data?.status === 'completed') {
        // Try the authenticated endpoint first; if the viewer is anonymous
        // (e.g. visiting a public share link) the auth call returns 401, so
        // fall back to the public points-table endpoint so spectators still
        // see the standings.
        let pt = {};
        try {
          const r = await tournamentsAPI.getPointsTable(tournamentId);
          pt = r.data || {};
        } catch {
          try {
            const r2 = await publicAPI.getPointsTable(tournamentId);
            pt = r2.data || {};
          } catch { /* leave empty — UI will show its own empty state */ }
        }
        setPointsTable(pt);
      }
    } catch (err) {
      toast.error('Failed to load tournament');
      navigate('/tournaments');
    } finally {
      setLoading(false);
    }
  }, [tournamentId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-select the most informative tab on first load:
  // - if pools exist → Points Table (the focal artifact)
  // - else if teams registered → Players tab
  // - else → Overview
  const didAutoSelect = React.useRef(false);
  useEffect(() => {
    if (didAutoSelect.current || !tournament) return;
    if ((tournament.pools || []).length > 0) setActiveTab('points');
    else if ((tournament.teams || []).length > 0) setActiveTab('teams');
    didAutoSelect.current = true;
  }, [tournament]);

  const teamsMap = {};
  if (tournament?.teams) {
    tournament.teams.forEach(t => { teamsMap[t.team_id] = t; });
  }

  // Best display label for a team. Mirrors the backend's _team_display_name
  // so legacy/incomplete teams still render both partners on doubles draws.
  const getTeamName = (teamId) => {
    const t = teamsMap[teamId];
    if (!t) return teamId;
    const nm = (t.team_name || '').trim();
    if (nm) return nm;
    const p1 = (t.player1_name || '').trim();
    const p2 = (t.player2_name || '').trim();
    if (p1 && p2) return `${p1} & ${p2}`;
    return p1 || teamId;
  };
  const myShadowId = (typeof window !== 'undefined') ? localStorage.getItem('loveall:shadow_id') : null;
  const isOrganizer = (
    tournament?.organizer_id === user?.user_id ||
    tournament?.organizer_user_id === user?.user_id ||
    (tournament?.organizer_shadow_id && tournament.organizer_shadow_id === myShadowId) ||
    ['platform_admin', 'tournament_admin'].includes(user?.role)
  );

  // Orphan teams = teams not yet placed in any pool. Drives the "Expand Bracket"
  // banner so organizers can absorb late entries (8 → 16 etc.) without losing data.
  const orphanTeamIds = useMemo(() => {
    if (!tournament?.pools || tournament.pools.length === 0) return [];
    const placed = new Set();
    tournament.pools.forEach(p => (p.team_ids || []).forEach(id => placed.add(id)));
    return (tournament.teams || []).map(t => t.team_id).filter(id => id && !placed.has(id));
  }, [tournament]);
  const knockoutAlreadyStarted = (tournament?.knockout?.rounds || []).length > 0;
  const canExpand = isOrganizer && orphanTeamIds.length > 0 && !knockoutAlreadyStarted;

  const handleExpandConfirm = async () => {
    setExpanding(true);
    try {
      const res = await tournamentsAPI.expand(tournamentId, { strategy: expandStrategy });
      toast.success(`Bracket expanded: ${res.data.new_matches_created} new matches${res.data.matches_deleted ? `, ${res.data.matches_deleted} replaced` : ''}`);
      setExpandDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not expand bracket');
    } finally {
      setExpanding(false);
    }
  };

  // Fetch the daily scoring PIN as soon as we know the caller is the organizer.
  useEffect(() => {
    if (!isOrganizer || !tournamentId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await tournamentsAPI.getScoringPin(tournamentId);
        if (!cancelled) setScoringPin(res.data);
      } catch (err) {
        // 403 if not authorised — silently ignore.
      }
    })();
    return () => { cancelled = true; };
  }, [isOrganizer, tournamentId]);

  /**
   * Wrapper that runs a scoring/start API call and, on 403 PIN_REQUIRED, opens
   * the PIN dialog and retries once the user enters the correct PIN.
   */
  const withPinPrompt = useCallback(async (action) => {
    try {
      return await action();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail === 'PIN_REQUIRED') {
        // Save the action; the dialog will retry it after a successful PIN entry.
        setPendingScoringAction(() => action);
        setPinDialogOpen(true);
        return null;
      }
      throw err;
    }
  }, []);

  const handlePinSubmit = useCallback(async (pin) => {
    // The PIN was already saved to sessionStorage by the dialog component.
    // Retry the pending action — if it succeeds, the PIN is valid.
    if (!pendingScoringAction) return false;
    try {
      await pendingScoringAction();
      setPinDialogOpen(false);
      setPendingScoringAction(null);
      toast.success('Scoring unlocked for this device');
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail === 'PIN_REQUIRED') {
        return false; // Wrong PIN — dialog will show error.
      }
      // Different error — surface it but close the dialog.
      toast.error(err?.response?.data?.detail || 'Action failed');
      setPinDialogOpen(false);
      setPendingScoringAction(null);
      return true;
    }
  }, [pendingScoringAction]);

  const handleAddTeam = async () => {
    if (!teamForm.player1_name.trim()) { toast.error('Player 1 name is required'); return; }
    // For doubles tournaments, both partners are required so the team_name
    // resolves to "Alice & Bob" (not just "Alice") in matches and the points
    // table.
    if (tournament?.match_type === 'doubles' && !teamForm.player2_name.trim()) {
      toast.error('Player 2 name is required for doubles');
      return;
    }
    try {
      await tournamentsAPI.addTeam(tournamentId, teamForm);
      if ((tournament?.pools || []).length > 0) {
        toast.success('Team added — use "Expand Bracket" on the Overview tab to fold them into the schedule');
      } else {
        toast.success('Player(s) registered');
      }
      setAddTeamOpen(false);
      setTeamForm({
        player1_name: '', player2_name: '', team_name: '',
        email: '', partner_email: '', mobile: '', partner_mobile: '',
        member_id: '', category: tournament?.match_type || 'doubles',
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add player(s)');
    }
  };

  const handleAddUmpire = async () => {
    if (!umpireForm.name.trim()) { toast.error('Umpire name required'); return; }
    try {
      await tournamentsAPI.addUmpire(tournamentId, umpireForm);
      toast.success('Umpire added');
      setAddUmpireOpen(false);
      setUmpireForm({ name: '', email: '', mobile: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add umpire');
    }
  };

  /** Send invite emails to all registered players. Falls back gracefully if
   *  SMTP isn't configured on the backend (admin will see a "configure SMTP" toast). */
  const handleInviteAll = async () => {
    const hasEmails = (tournament?.teams || []).some(t => t.email || t.partner_email);
    if (!hasEmails) { toast.error('Add players with email addresses first.'); return; }
    setInviting(true);
    try {
      const res = await tournamentsAPI.invite(tournamentId, {});
      if (res.data.sent > 0) {
        toast.success(`Sent ${res.data.sent} invite${res.data.sent === 1 ? '' : 's'}`);
      } else if (!res.data.mailer_configured) {
        toast.error('Email not configured yet. Opening WhatsApp invite instead.');
        handleWhatsAppInvite();
      } else {
        toast.error(`No invites sent (${res.data.failed} failed)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invites');
    } finally {
      setInviting(false);
    }
  };

  /** Share the live tournament link on WhatsApp — short emoji caption + URL. */
  const handleBroadcastStandings = async () => {
    const link = `${window.location.origin}/tournaments/${tournamentId}`;
    const text = `🏸 ${tournament.name} — live standings & matches:\n${link}`;
    if (navigator.share) {
      await navigator.share({ url: link, text }).catch(() => {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  /** Primary WhatsApp invite for the tournament (used when teams are short). */
  const handleWhatsAppInvite = () => {
    const link = `${window.location.origin}/tournaments/${tournamentId}`;
    const max = tournament?.max_participants || 0;
    const current = tournament?.teams?.length || 0;
    const need = Math.max(0, max - current);
    const callout = need > 0 ? `Looking for ${need} more team${need === 1 ? '' : 's'}!` : 'Join us!';
    const text = `🏸 You're invited to *${tournament.name}*!\n${callout}\n\n👉 Register here:\n${link}`;
    if (navigator.share) {
      navigator.share({ url: link, text }).catch(() => {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const openEditCount = () => {
    setEditCountValue(String(tournament?.max_participants || ''));
    setEditCountOpen(true);
  };

  const handleSaveCount = async () => {
    const n = parseInt(editCountValue, 10);
    if (Number.isNaN(n) || n < 2 || n > 256) {
      toast.error('Team count must be between 2 and 256');
      return;
    }
    if (n === tournament.max_participants) {
      setEditCountOpen(false);
      return;
    }
    if (n < (tournament.teams?.length || 0)) {
      toast.error(`You already have ${tournament.teams.length} teams. Remove some before lowering the cap.`);
      return;
    }
    setSavingCount(true);
    try {
      await tournamentsAPI.patchTournament(tournamentId, { max_participants: n });
      toast.success(`Team cap updated to ${n}`);
      setEditCountOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update team count');
    } finally {
      setSavingCount(false);
    }
  };

  const handleRemoveUmpire = async (umpireId) => {
    try {
      await tournamentsAPI.removeUmpire(tournamentId, umpireId);
      toast.success('Umpire removed');
      fetchData();
    } catch {
      toast.error('Failed to remove umpire');
    }
  };

  const handleGenerateSuperPools = async () => {
    const numStr = window.prompt('How many new pools? (e.g. 2 for Super 8)', '2');
    if (!numStr) return;
    const n = parseInt(numStr, 10);
    if (Number.isNaN(n) || n < 2) { toast.error('Enter a valid number ≥ 2'); return; }
    const topNStr = window.prompt('Top how many teams from each pool advance?', '2');
    const topN = parseInt(topNStr, 10) || 2;
    try {
      await tournamentsAPI.generateSuperPools(tournamentId, { num_pools: n, top_n: topN });
      toast.success('Next-round pools generated');
      fetchData();
      setActiveTab('matches');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate super pools');
    }
  };

  const handleDirectKnockout = async () => {
    if (!window.confirm('Generate knockout bracket directly (no pools)? This cannot be undone.')) return;
    try {
      await tournamentsAPI.generateDirectKnockout(tournamentId, {});
      toast.success('Knockout bracket created');
      fetchData();
      setActiveTab('knockout');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate knockout');
    }
  };

  const openStartMatch = (match) => {
    setMatchToStart(match);
    setSelectedUmpireId('');
    setStartMatchOpen(true);
  };

  const confirmStartMatch = async () => {
    if (!matchToStart) return;
    const action = async () => {
      await tournamentsAPI.startMatch(tournamentId, matchToStart.match_id, {
        umpire_id: selectedUmpireId || user?.user_id,
      });
    };
    try {
      const result = await withPinPrompt(action);
      if (result === null) return; // PIN dialog opened; will retry from there
      toast.success('Match started');
      setStartMatchOpen(false);
      fetchData();
      // Open the scoring dialog inline (uses existing dialog)
      const updated = matches.find(m => m.match_id === matchToStart.match_id) || matchToStart;
      openScoreDialog({ ...updated, status: 'live' });
    } catch (err) {
      toast.error('Failed to start match');
    }
  };

  const handleRemoveTeam = async (teamId) => {
    const midTournament = (tournament?.pools || []).length > 0;
    if (midTournament && !window.confirm('Remove this team? Their pending matches will be deleted — completed results stay in the standings.')) return;
    try {
      const res = await tournamentsAPI.removeTeam(tournamentId, teamId);
      const n = res?.data?.matches_deleted || 0;
      toast.success(n > 0 ? `Team removed — ${n} pending match${n === 1 ? '' : 'es'} deleted` : 'Team removed');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove team');
    }
  };

  const handleCreatePools = async () => {
    setCreatingPools(true);
    try {
      await tournamentsAPI.createPools(tournamentId, {
        num_pools: parseInt(numPools),
        pool_assignments: poolPreview,
      });
      toast.success('Pools created! Matches generated.');
      setPoolPreview(null);
      fetchData();
      setActiveTab('matches');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create pools');
    } finally {
      setCreatingPools(false);
    }
  };

  const generatePoolPreview = () => {
    const teams = [...(tournament?.teams || [])];
    const n = parseInt(numPools);
    // Shuffle
    for (let i = teams.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [teams[i], teams[j]] = [teams[j], teams[i]];
    }
    const preview = {};
    for (let i = 0; i < n; i++) {
      preview[chr(65 + i)] = [];
    }
    teams.forEach((team, idx) => {
      const poolName = chr(65 + (idx % n));
      preview[poolName].push(team.team_id);
    });
    setPoolPreview(preview);
  };

  const reshufflePools = () => generatePoolPreview();

  const moveTeamToPool = (teamId, fromPool, toPool) => {
    if (!poolPreview || fromPool === toPool) return;
    const updated = { ...poolPreview };
    updated[fromPool] = updated[fromPool].filter(id => id !== teamId);
    updated[toPool] = [...(updated[toPool] || []), teamId];
    setPoolPreview(updated);
  };

  const openEditTeam = (team) => {
    setEditingTeam(team);
    setEditForm({ team_name: team.team_name, player1_name: team.player1_name, player2_name: team.player2_name || '' });
    setEditTeamOpen(true);
  };

  const handleEditTeam = async () => {
    if (!editingTeam) return;
    try {
      await tournamentsAPI.updateTeam(tournamentId, editingTeam.team_id, editForm);
      toast.success('Team updated');
      setEditTeamOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to update team');
    }
  };

  const handleStartMatch = (matchId) => {
    const m = matches.find(x => x.match_id === matchId);
    if (!m) return;
    openStartMatch(m);
  };

  const openScoreDialog = async (match) => {
    // Refetch so live-synced scores (auto-save) are restored on reopen.
    let fresh = match;
    try {
      const res = await tournamentsAPI.getMatches(tournamentId);
      const found = (res.data || []).find(m => m.match_id === match.match_id);
      if (found) fresh = found;
    } catch { /* fall back to the cached match */ }
    setScoringMatch(fresh);
    setGameScores(fresh.scores?.length > 0 ? fresh.scores : [{ team1_score: 0, team2_score: 0 }]);
    setSelectedWinner(fresh.winner_id || '');
    setScoreDialogOpen(true);
  };

  const handleSubmitScore = async (payload) => {
    // payload comes from TournamentMatchScorer: { games, winner_id }
    if (!payload?.winner_id) { toast.error('No winner yet'); return; }
    const action = async () => {
      await tournamentsAPI.scoreMatch(tournamentId, scoringMatch.match_id, {
        games: payload.games, winner_id: payload.winner_id,
      });
    };
    try {
      const result = await withPinPrompt(action);
      if (result === null) return; // PIN dialog opened; retry from there
      toast.success('Score recorded');
      setScoreDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save score');
    }
  };

  // Fire-and-forget push of in-progress scores so spectators see them live
  // and the score survives closing the dialog (auto-save/recovery).
  const pushLiveScore = useCallback((games) => {
    if (!scoringMatch) return;
    tournamentsAPI.liveScore(tournamentId, scoringMatch.match_id, { games }).catch(() => {});
  }, [scoringMatch, tournamentId]);

  const handleShuffleOrder = async () => {
    try {
      const res = await tournamentsAPI.shuffleMatches(tournamentId);
      toast.success(`Shuffled ${res.data.reordered} pending matches for fairer spacing`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to shuffle match order');
    }
  };

  const handleWalkover = async (winnerId) => {
    if (!walkoverMatch) return;
    const matchId = walkoverMatch.match_id;
    const action = async () => {
      await tournamentsAPI.walkover(tournamentId, matchId, { winner_id: winnerId });
      toast.success('Walkover recorded — 0-0, points awarded');
      setWalkoverMatch(null);
      fetchData();
    };
    try {
      await withPinPrompt(action);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record walkover');
    }
  };

  const handleGenerateKnockout = async () => {
    try {
      await tournamentsAPI.generateKnockout(tournamentId, { top_n: 2 });
      toast.success('Knockout stage generated');
      fetchData();
      setActiveTab('knockout');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate knockout');
    }
  };

  const handleGenerateNextRound = async (prevRound, nextRound) => {
    try {
      await tournamentsAPI.generateNextRound(tournamentId, { prev_round: prevRound, round_name: nextRound });
      toast.success(`${nextRound} generated`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate next round');
    }
  };

  const shareUrl = `${window.location.origin}/live/tournament/${tournamentId}`;

  const buildShareText = () => {
    const lines = [`${tournament?.name || 'Tournament'} - Live Scores`];
    if (tournament?.status === 'in_progress') lines.push('Status: In Progress');
    const liveCount = matches.filter(m => m.status === 'live').length;
    const completedCount = matches.filter(m => m.status === 'completed').length;
    if (liveCount > 0) lines.push(`${liveCount} match(es) LIVE now!`);
    if (completedCount > 0) lines.push(`${completedCount} match(es) completed`);
    lines.push('', `Follow live: ${shareUrl}`);
    return lines.join('\n');
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
    } catch {
      toast.info(shareUrl);
    }
  };

  const handleWhatsAppShare = () => {
    const url = `${window.location.origin}/tournaments/${tournamentId}`;
    const text = `🏸 ${tournament.name} — view the draw, matches & standings:\n${url}`;
    if (navigator.share) {
      navigator.share({ url, text }).catch(() => { window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank'); });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-[400px]" />
        </div>
      </Layout>
    );
  }

  if (!tournament) return null;

  const poolMatches = matches.filter(m => m.stage === 'pool');
  const knockoutMatches = matches.filter(m => m.stage === 'knockout');
  // Live first, pending next (in play order), completed sink to the bottom.
  const statusRank = { live: 0, pending: 1, completed: 2 };
  const sortByPlayOrder = (arr) => [...arr].sort((a, b) =>
    (statusRank[a.status] ?? 1) - (statusRank[b.status] ?? 1) ||
    ((a.play_order ?? a.match_number ?? 0) - (b.play_order ?? b.match_number ?? 0))
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="tournament-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <Trophy className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight" data-testid="tournament-name">
                  {tournament.name}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={cn(
                    tournament.status === 'registration' && 'bg-blue-500',
                    tournament.status === 'in_progress' && 'bg-green-500',
                    tournament.status === 'completed' && 'bg-gray-500',
                  )} data-testid="tournament-status">{tournament.status.replace('_', ' ')}</Badge>
                  <span className="text-sm text-muted-foreground capitalize">{tournament.format.replace('_', ' ')} / {tournament.match_type}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
              {tournament.organizer_name && (
                <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{tournament.organizer_name}</span>
              )}
              {tournament.organizer_phone && (
                <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{tournament.organizer_phone}</span>
              )}
              {tournament.start_date && (
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatDate(tournament.start_date)}</span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleWhatsAppShare} data-testid="whatsapp-share-btn" className="text-green-500 border-green-500/30 hover:bg-green-500/10">
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </Button>
            <Button variant="outline" onClick={handleShare} data-testid="share-tournament-btn">
              <Copy className="mr-2 h-4 w-4" />Copy Link
            </Button>
            {/* Scoring PIN pill — only the creator sees it. Tap to reveal/hide. */}
            {isOrganizer && scoringPin && (
              <button
                type="button"
                onClick={() => setShowPin(s => !s)}
                className="inline-flex items-center gap-2 rounded-md border border-accent/40 bg-accent/5 px-3 py-2 text-sm font-semibold transition-colors hover:bg-accent/10"
                title="Scoring PIN — constant for this tournament. Do not share publicly."
                data-testid="scoring-pin-pill"
              >
                <Lock className="h-3.5 w-3.5 text-accent" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">PIN</span>
                <span className="font-mono text-base tracking-[0.2em] text-accent" data-testid="scoring-pin-value">
                  {showPin ? scoringPin.pin : '••••'}
                </span>
                {showPin ? <EyeOff className="h-3.5 w-3.5 text-muted-foreground" /> : <Eye className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start overflow-x-auto" data-testid="tournament-tabs">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="teams">Players ({tournament.teams?.length || 0})</TabsTrigger>
            <TabsTrigger value="umpires" data-testid="umpires-tab">Umpires ({umpires?.length || 0})</TabsTrigger>
            {tournament.pools?.length > 0 && <TabsTrigger value="matches">Matches</TabsTrigger>}
            {tournament.pools?.length > 0 && <TabsTrigger value="points">Points Table</TabsTrigger>}
            {knockoutMatches.length > 0 && <TabsTrigger value="knockout">Knockout</TabsTrigger>}
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="mt-6 space-y-4">
            <ChampionBragCard
              tournament={tournament}
              matches={matches}
              tournamentId={tournamentId}
              getTeamName={getTeamName}
            />

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card><CardContent className="p-4 text-center"><div className="text-2xl font-heading font-bold">{tournament.teams?.length || 0}</div><div className="text-xs text-muted-foreground">Teams</div></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><div className="text-2xl font-heading font-bold">{matches.length}</div><div className="text-xs text-muted-foreground">Matches</div></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><div className="text-2xl font-heading font-bold">{matches.filter(m => m.status === 'completed').length}</div><div className="text-xs text-muted-foreground">Completed</div></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><div className="text-2xl font-heading font-bold">{matches.filter(m => m.status === 'live').length}</div><div className="text-xs text-muted-foreground">Live Now</div></CardContent></Card>
            </div>

            {/* Expand Bracket banner — appears when orphan teams exist and knockout hasn't started */}
            {canExpand && (
              <ExpandBracketBanner
                orphanTeamIds={orphanTeamIds}
                onExpand={() => { setExpandStrategy('append'); setExpandDialogOpen(true); }}
              />
            )}

            {/* "Feature on landing" toggle — organizer-only */}
            {isOrganizer && (
              <Card className="border-amber-400/30 bg-gradient-to-br from-amber-500/5 to-transparent" data-testid="feature-on-landing-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-5 w-5 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">Feature on home page</div>
                    <div className="text-xs text-muted-foreground">Pin this tournament to the public landing page so visitors see it first.</div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!tournament.featured}
                    onClick={async () => {
                      const desired = !tournament.featured;
                      try {
                        await tournamentsAPI.setFeatured(tournamentId, { featured: desired });
                        setTournament({ ...tournament, featured: desired });
                        toast.success(desired ? 'Pinned to landing page' : 'Removed from landing page');
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not update');
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${tournament.featured ? 'bg-amber-500' : 'bg-muted'}`}
                    data-testid="feature-toggle"
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${tournament.featured ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </CardContent>
              </Card>
            )}

            {/* Scoring Access card — visible only to the tournament creator. */}
            {isOrganizer && scoringPin && (
              <ScoringAccessCard
                scoringPin={scoringPin}
                showPin={showPin}
                setShowPin={setShowPin}
              />
            )}
            <Card>
              <CardHeader><CardTitle className="text-base">Tournament Settings</CardTitle></CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Points per game:</span> {tournament.points_per_game}</div>
                <div><span className="text-muted-foreground">Best of:</span> {tournament.best_of} game(s)</div>
                <div><span className="text-muted-foreground">Golden point:</span> {tournament.golden_point ? 'Yes' : 'No'}</div>
                <div><span className="text-muted-foreground">Shuttle type:</span> {tournament.shuttle_type}</div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TEAMS TAB */}
          <TabsContent value="teams" className="mt-6 space-y-4">
            {/* Edit Team Count — organizer-only, always visible when tournament is still ongoing */}
            {isOrganizer && tournament.status !== 'completed' && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEditCount}
                  className="border-secondary/40 hover:bg-secondary/10"
                  data-testid="edit-team-count-btn"
                >
                  <Users className="mr-2 h-4 w-4" />
                  Edit Team Count
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    ({tournament.teams?.length || 0}/{tournament.max_participants || 0})
                  </span>
                </Button>
              </div>
            )}

            {/* "Need More Teams?" banner — visible to the organizer when registrations are below max */}
            {isOrganizer && tournament.status === 'registration' && (() => {
              const max = tournament.max_participants || 0;
              const current = tournament.teams?.length || 0;
              const need = max - current;
              if (need <= 0) return null;
              return (
                <Card className="border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-amber-500/5 to-transparent" data-testid="need-more-teams-banner">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                          Need {need} more team{need === 1 ? '' : 's'}
                        </p>
                        <p className="font-heading text-base sm:text-lg font-extrabold mt-0.5 leading-tight">
                          {current} of {max} teams registered. Invite players to fill the draw 🏸
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          WhatsApp is the fastest way. Email is the fallback for players whose number you don&apos;t have.
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          onClick={handleWhatsAppInvite}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="invite-whatsapp-btn"
                        >
                          <span className="mr-1.5 text-base">📱</span>WhatsApp
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleInviteAll}
                          disabled={inviting || !(tournament.teams || []).some(t => t.email || t.partner_email)}
                          className="border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10"
                          data-testid="invite-email-btn"
                          title={!(tournament.teams || []).some(t => t.email || t.partner_email) ? 'Add players with email to enable email invites' : ''}
                        >
                          <span className="mr-1.5 text-base">📧</span>{inviting ? 'Sending…' : 'Email'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {isOrganizer && tournament.status !== 'completed' && (
              <div className="flex flex-wrap items-center gap-3">
                <Dialog open={addTeamOpen} onOpenChange={setAddTeamOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-secondary hover:bg-secondary/90" data-testid="add-team-btn">
                      <Plus className="mr-2 h-4 w-4" />Register Player{tournament.match_type === 'doubles' ? '/Team' : ''}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Register Player{tournament.match_type === 'doubles' ? 's / Team' : ''}</DialogTitle>
                      <DialogDescription>Only the player name is required. Email is optional — add it later if you need to email-invite a player you can&apos;t reach by WhatsApp.</DialogDescription>                    </DialogHeader>
                    <div className="space-y-3 py-3">
                      <div>
                        <Label>Team Name</Label>
                        <Input placeholder="e.g. Arun & Kumar (optional — auto-built if blank)" value={teamForm.team_name} onChange={e => setTeamForm({ ...teamForm, team_name: e.target.value })} data-testid="team-name-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>Player 1 Name *</Label>
                          <Input placeholder="Player 1" value={teamForm.player1_name} onChange={e => setTeamForm({ ...teamForm, player1_name: e.target.value })} data-testid="player1-name-input" />
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Player 1 Email <span className="text-[10px] font-normal">(optional)</span></Label>
                          <Input type="email" placeholder="for email invite fallback" value={teamForm.email} onChange={e => setTeamForm({ ...teamForm, email: e.target.value })} data-testid="player1-email-input" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Mobile (optional)</Label>
                          <Input placeholder="+91…" value={teamForm.mobile} onChange={e => setTeamForm({ ...teamForm, mobile: e.target.value })} data-testid="player1-mobile-input" />
                        </div>
                        <div>
                          <Label className="text-xs">Member ID (optional)</Label>
                          <Input placeholder="e.g. M1043" value={teamForm.member_id} onChange={e => setTeamForm({ ...teamForm, member_id: e.target.value })} data-testid="member-id-input" />
                        </div>
                      </div>
                      {tournament.match_type === 'doubles' && (
                        <>
                          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
                            <div>
                              <Label>Player 2 Name <span className="text-destructive">*</span></Label>
                              <Input placeholder="Player 2 (required)" required value={teamForm.player2_name} onChange={e => setTeamForm({ ...teamForm, player2_name: e.target.value })} data-testid="player2-name-input" />
                            </div>
                            <div>
                              <Label className="text-muted-foreground">Player 2 Email <span className="text-[10px] font-normal">(optional)</span></Label>
                              <Input type="email" placeholder="for email invite fallback" value={teamForm.partner_email} onChange={e => setTeamForm({ ...teamForm, partner_email: e.target.value })} data-testid="player2-email-input" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Player 2 Mobile (optional)</Label>
                            <Input placeholder="+91…" value={teamForm.partner_mobile} onChange={e => setTeamForm({ ...teamForm, partner_mobile: e.target.value })} data-testid="player2-mobile-input" />
                          </div>
                        </>
                      )}
                      <div>
                        <Label className="text-xs">Category</Label>
                        <Select value={teamForm.category} onValueChange={(v) => setTeamForm({ ...teamForm, category: v })}>
                          <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="singles">Singles</SelectItem>
                            <SelectItem value="doubles">Doubles</SelectItem>
                            <SelectItem value="mens_singles">Men&apos;s Singles</SelectItem>
                            <SelectItem value="womens_singles">Women&apos;s Singles</SelectItem>
                            <SelectItem value="mens_doubles">Men&apos;s Doubles</SelectItem>
                            <SelectItem value="womens_doubles">Women&apos;s Doubles</SelectItem>
                            <SelectItem value="mixed_doubles">Mixed Doubles</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddTeamOpen(false)}>Cancel</Button>
                      <Button onClick={handleAddTeam} className="bg-secondary hover:bg-secondary/90" data-testid="submit-team-btn">Register</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {tournament.status === 'registration' && tournament.teams?.length >= 2 && !poolPreview && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select value={numPools} onValueChange={setNumPools}>
                      <SelectTrigger className="w-32" data-testid="num-pools-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Pool</SelectItem>
                        <SelectItem value="2">2 Pools</SelectItem>
                        <SelectItem value="3">3 Pools</SelectItem>
                        <SelectItem value="4">4 Pools</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={generatePoolPreview} data-testid="preview-pools-btn">
                      Generate Pools
                    </Button>
                    <span className="text-xs text-muted-foreground">or</span>
                    <Button variant="outline" onClick={handleDirectKnockout} data-testid="direct-knockout-btn">
                      <Crown className="mr-1 h-3.5 w-3.5" />Direct Knockout
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Pool Preview */}
            {poolPreview && tournament.status === 'registration' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold">Pool Preview</h3>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={reshufflePools} data-testid="reshuffle-btn">
                      <Shuffle className="mr-1 h-3.5 w-3.5" />Reshuffle
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPoolPreview(null)}>Cancel</Button>
                    <Button size="sm" onClick={handleCreatePools} disabled={creatingPools} className="bg-secondary hover:bg-secondary/90" data-testid="confirm-pools-btn">
                      {creatingPools ? 'Creating...' : 'Confirm & Start'}
                    </Button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(poolPreview).map(([poolName, teamIds]) => (
                    <Card key={poolName} className="border-dashed">
                      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Pool {poolName}</CardTitle></CardHeader>
                      <CardContent className="space-y-2">
                        {teamIds.map(tid => {
                          const team = teamsMap[tid];
                          if (!team) return null;
                          return (
                            <div key={tid} className="flex items-center justify-between p-2 rounded bg-muted/50 text-sm group" data-testid={`preview-team-${tid}`}>
                              <span className="truncate">{team.team_name}</span>
                              <Select onValueChange={(toPool) => moveTeamToPool(tid, poolName, toPool)}>
                                <SelectTrigger className="w-16 h-7 text-xs opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`move-team-${tid}`}>
                                  <ArrowLeftRight className="h-3 w-3" />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.keys(poolPreview).filter(p => p !== poolName).map(p => (
                                    <SelectItem key={p} value={p}>Pool {p}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        })}
                        {teamIds.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">Empty</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Team Cards */}
            {!poolPreview && (
              <>
                {!tournament.teams?.length ? (
                  <Card><CardContent className="py-12 text-center"><Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" /><p className="text-muted-foreground">No teams registered yet</p></CardContent></Card>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {tournament.teams.map((team, idx) => (
                      <Card key={team.team_id} className="relative group" data-testid={`team-card-${idx}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold">{idx + 1}</div>
                              <div>
                                <div className="font-medium">{team.team_name}</div>
                                <div className="text-xs text-muted-foreground">{team.player1_name}{team.player2_name ? ` & ${team.player2_name}` : ''}</div>
                                {(team.email || team.member_id) && (
                                  <div className="text-[10px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2">
                                    {team.email && <span data-testid={`team-email-${idx}`}>{team.email}</span>}
                                    {team.member_id && <span>· #{team.member_id}</span>}
                                    {team.user_id && <span className="text-green-500">· linked</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                            {isOrganizer && tournament.status !== 'completed' && (
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button size="icon" variant="ghost" onClick={() => openEditTeam(team)} data-testid={`edit-team-${idx}`}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => handleRemoveTeam(team.team_id)} data-testid={`remove-team-${idx}`}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Edit Team Dialog */}
            <Dialog open={editTeamOpen} onOpenChange={setEditTeamOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Team</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-3">
                  <div><Label>Team Name</Label><Input value={editForm.team_name} onChange={e => setEditForm({ ...editForm, team_name: e.target.value })} data-testid="edit-team-name-input" /></div>
                  <div><Label>Player 1</Label><Input value={editForm.player1_name} onChange={e => setEditForm({ ...editForm, player1_name: e.target.value })} data-testid="edit-player1-input" /></div>
                  {tournament.match_type === 'doubles' && (
                    <div><Label>Player 2</Label><Input value={editForm.player2_name} onChange={e => setEditForm({ ...editForm, player2_name: e.target.value })} data-testid="edit-player2-input" /></div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditTeamOpen(false)}>Cancel</Button>
                  <Button onClick={handleEditTeam} className="bg-secondary hover:bg-secondary/90" data-testid="save-edit-team-btn">Save Changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* MATCHES TAB */}
          <TabsContent value="matches" className="mt-6 space-y-6">
            {/* Shuffle match order — organizer-only, needs 2+ pending matches */}
            {isOrganizer && poolMatches.filter(m => m.status === 'pending').length > 1 && (
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={handleShuffleOrder} data-testid="shuffle-order-btn">
                  <Shuffle className="mr-2 h-4 w-4" />Shuffle Match Order
                </Button>
              </div>
            )}
            {tournament.pools?.map(pool => {
              const pMatches = sortByPlayOrder(poolMatches.filter(m => m.pool_name === pool.pool_name));
              return (
                <div key={pool.pool_name}>
                  <h3 className="font-heading text-lg font-bold mb-3">Pool {pool.pool_name}</h3>
                  <div className="space-y-2">
                    {pMatches.map(match => (
                      <MatchCard key={match.match_id} match={match} getTeamName={getTeamName} isOrganizer={isOrganizer} onStart={handleStartMatch} onScore={openScoreDialog} onWalkover={setWalkoverMatch} />
                    ))}
                    {pMatches.length === 0 && <p className="text-sm text-muted-foreground">No matches in this pool</p>}
                  </div>
                </div>
              );
            })}

            {/* Generate Knockout / Super 8 buttons */}
            {isOrganizer && tournament.pools?.length > 0 && poolMatches.every(m => m.status === 'completed') && poolMatches.length > 0 && !tournament.knockout?.rounds?.length && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleGenerateKnockout} className="bg-accent text-accent-foreground" data-testid="generate-knockout-btn">
                  <Crown className="mr-2 h-4 w-4" />Generate Knockout Stage
                </Button>
                {tournament.pools?.length > 2 && (
                  <Button variant="outline" onClick={handleGenerateSuperPools} data-testid="generate-super-pools-btn">
                    <Shuffle className="mr-2 h-4 w-4" />Generate Super Pools (Round 2)
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          {/* UMPIRES TAB */}
          <TabsContent value="umpires" className="mt-6 space-y-4">
            {isOrganizer && (
              <Dialog open={addUmpireOpen} onOpenChange={setAddUmpireOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-secondary hover:bg-secondary/90" data-testid="add-umpire-btn">
                    <Plus className="mr-2 h-4 w-4" />Add Umpire
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Umpire</DialogTitle>
                    <DialogDescription>Umpires can be selected when starting a match.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 py-3">
                    <div><Label>Name *</Label><Input value={umpireForm.name} onChange={e => setUmpireForm({ ...umpireForm, name: e.target.value })} data-testid="umpire-name-input" /></div>
                    <div><Label>Email</Label><Input type="email" value={umpireForm.email} onChange={e => setUmpireForm({ ...umpireForm, email: e.target.value })} data-testid="umpire-email-input" /></div>
                    <div><Label>Mobile</Label><Input value={umpireForm.mobile} onChange={e => setUmpireForm({ ...umpireForm, mobile: e.target.value })} data-testid="umpire-mobile-input" /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddUmpireOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddUmpire} className="bg-secondary hover:bg-secondary/90" data-testid="submit-umpire-btn">Add Umpire</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {umpires.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-muted-foreground">No umpires yet. Add umpires so they can be assigned to matches.</CardContent></Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {umpires.map((u, idx) => (
                  <Card key={u.umpire_id} className="relative group" data-testid={`umpire-card-${idx}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{u.name}</div>
                          {u.email && <div className="text-xs text-muted-foreground">{u.email}</div>}
                          {u.mobile && <div className="text-xs text-muted-foreground">{u.mobile}</div>}
                        </div>
                        {isOrganizer && (
                          <Button size="icon" variant="ghost" onClick={() => handleRemoveUmpire(u.umpire_id)} data-testid={`remove-umpire-${idx}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* POINTS TABLE TAB */}
          <TabsContent value="points" className="mt-6 space-y-6">
            {Object.keys(pointsTable).length > 0 && (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="font-heading text-xl font-extrabold flex items-center gap-2">
                    📊 Standings <span className="text-xs font-normal text-muted-foreground">— Auto-Calculated Points Table</span>
                  </h2>
                </div>
                <Button
                  onClick={handleBroadcastStandings}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="broadcast-standings-btn"
                >
                  <Radio className="mr-2 h-4 w-4" />Share Live Link via WhatsApp
                </Button>
              </div>
            )}
            {Object.entries(pointsTable).map(([poolName, standings]) => (
              <div key={poolName}>
                <h3 className="font-heading text-lg font-bold mb-3">Pool {poolName}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`points-table-${poolName}`}>
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left p-2 font-medium">#</th>
                        <th className="text-left p-2 font-medium">Team</th>
                        <th className="text-center p-2 font-medium">P</th>
                        <th className="text-center p-2 font-medium">W</th>
                        <th className="text-center p-2 font-medium">L</th>
                        <th className="text-center p-2 font-medium">PF</th>
                        <th className="text-center p-2 font-medium">PA</th>
                        <th className="text-center p-2 font-medium">Diff</th>
                        <th className="text-center p-2 font-medium font-bold">Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((row, idx) => (
                        <tr key={row.team_id} className={cn('border-b', idx < 2 && 'bg-accent/5')}>
                          <td className="p-2 font-mono">{idx + 1}</td>
                          <td className="p-2 font-medium">{row.team_name}</td>
                          <td className="p-2 text-center font-mono">{row.played}</td>
                          <td className="p-2 text-center font-mono text-green-500">{row.won}</td>
                          <td className="p-2 text-center font-mono text-red-400">{row.lost}</td>
                          <td className="p-2 text-center font-mono">{row.points_scored}</td>
                          <td className="p-2 text-center font-mono">{row.points_conceded}</td>
                          <td className="p-2 text-center font-mono">{row.score_diff > 0 ? '+' : ''}{row.score_diff}</td>
                          <td className="p-2 text-center font-mono font-bold">{row.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {Object.keys(pointsTable).length === 0 && (
              <Card><CardContent className="py-12 text-center text-muted-foreground">Points table will appear once pool matches are played</CardContent></Card>
            )}
          </TabsContent>

          {/* KNOCKOUT TAB */}
          <TabsContent value="knockout" className="mt-6 space-y-6">
            {/* View toggle */}
            {tournament.knockout?.rounds?.length > 0 && (
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                  <Crown className="h-5 w-5 text-accent" />Bracket
                </h3>
                <div className="inline-flex rounded-md border bg-card p-0.5" role="tablist">
                  <button
                    type="button"
                    onClick={() => setKnockoutView('bracket')}
                    className={cn(
                      'px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded transition-colors',
                      knockoutView === 'bracket' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground'
                    )}
                    data-testid="knockout-view-bracket"
                  >
                    Bracket
                  </button>
                  <button
                    type="button"
                    onClick={() => setKnockoutView('list')}
                    className={cn(
                      'px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded transition-colors',
                      knockoutView === 'list' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground'
                    )}
                    data-testid="knockout-view-list"
                  >
                    List
                  </button>
                </div>
              </div>
            )}

            {/* Bracket tree view */}
            {knockoutView === 'bracket' && tournament.knockout?.rounds?.length > 0 && (
              <>
                <BracketTreeView
                  tournament={tournament}
                  matches={knockoutMatches}
                  getTeamName={getTeamName}
                  isOrganizer={isOrganizer}
                  onScoreMatch={openScoreDialog}
                  onStartMatch={handleStartMatch}
                />
                {/* "Generate next round" buttons live below the bracket so organizers can still progress */}
                {isOrganizer && (() => {
                  const lastRound = tournament.knockout.rounds[tournament.knockout.rounds.length - 1];
                  const lastRoundMatches = knockoutMatches.filter(m => m.round === lastRound.round);
                  const allCompleted = lastRoundMatches.length > 0 && lastRoundMatches.every(m => m.status === 'completed');
                  const advancingCount = lastRoundMatches.filter(m => m.status === 'completed' && m.winner_id).length + ((lastRound.byes || []).length);
                  const isFinal = lastRound.round === 'Final';
                  const roundNameMap = (n) => {
                    if (n <= 2) return 'Final';
                    if (n <= 4) return 'Semi Final';
                    if (n <= 8) return 'Quarter Final';
                    if (n <= 16) return 'Round of 16';
                    if (n <= 32) return 'Round of 32';
                    return `Round of ${n}`;
                  };
                  const nextRound = roundNameMap(advancingCount);
                  if (!allCompleted || isFinal || advancingCount < 2) return null;
                  return (
                    <div className="flex justify-center">
                      <Button onClick={() => handleGenerateNextRound(lastRound.round, nextRound)} data-testid="generate-next-round-btn">
                        <ArrowRight className="mr-2 h-4 w-4" />Generate {nextRound}
                      </Button>
                    </div>
                  );
                })()}
              </>
            )}

            {/* Legacy list view (still useful for scoring on small phones) */}
            {knockoutView === 'list' && tournament.knockout?.rounds?.map((round, rIdx) => {
              const roundMatches = knockoutMatches.filter(m => m.round === round.round);
              const allCompleted = roundMatches.length > 0 && roundMatches.every(m => m.status === 'completed');
              const isFinal = round.round === 'Final';
              // How many teams advance from this round? winners of matches + any byes carried forward.
              const advancingCount = (roundMatches.filter(m => m.status === 'completed' && m.winner_id).length) + ((round.byes || []).length);
              const roundNameMap = (n) => {
                if (n <= 2) return 'Final';
                if (n <= 4) return 'Semi Final';
                if (n <= 8) return 'Quarter Final';
                if (n <= 16) return 'Round of 16';
                if (n <= 32) return 'Round of 32';
                return `Round of ${n}`;
              };
              const nextRound = roundNameMap(advancingCount);
              const byes = round.byes || [];

              return (
                <div key={rIdx}>
                  <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
                    <Crown className="h-5 w-5 text-accent" />{round.round}
                  </h3>
                  <div className="space-y-2">
                    {roundMatches.map(match => (
                      <MatchCard key={match.match_id} match={match} getTeamName={getTeamName} isOrganizer={isOrganizer} onStart={handleStartMatch} onScore={openScoreDialog} onWalkover={setWalkoverMatch} />
                    ))}
                  </div>
                  {/* Show teams that received a bye in this round */}
                  {byes.length > 0 && (
                    <div className="mt-3 rounded-md border border-dashed border-accent/40 bg-accent/5 p-3" data-testid={`round-byes-${rIdx}`}>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Byes — auto-advance</div>
                      <div className="flex flex-wrap gap-2">
                        {byes.map(tid => (
                          <span key={tid} className="text-sm font-medium px-2 py-1 rounded bg-background border">
                            {getTeamName(tid)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Check if we need to generate next round */}
                  {isOrganizer && allCompleted && !isFinal && advancingCount >= 2 && (
                    <Button className="mt-3" onClick={() => handleGenerateNextRound(round.round, nextRound)} data-testid="generate-next-round-btn-list">
                      <ArrowRight className="mr-2 h-4 w-4" />Generate {nextRound}
                    </Button>
                  )}
                  {/* Show champion */}
                  {isFinal && allCompleted && roundMatches[0]?.winner_id && (
                    <Card className="mt-4 border-accent/50 bg-accent/5">
                      <CardContent className="p-6 text-center">
                        <Crown className="h-10 w-10 text-accent mx-auto mb-2" />
                        <div className="font-heading text-xl font-bold uppercase">Champion</div>
                        <div className="text-lg mt-1">{getTeamName(roundMatches[0].winner_id)}</div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              );
            })}
            {knockoutMatches.length === 0 && (
              <Card><CardContent className="py-12 text-center text-muted-foreground">Knockout stage has not started yet</CardContent></Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Start Match (umpire select) Dialog */}
        <Dialog open={startMatchOpen} onOpenChange={setStartMatchOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Start Match</DialogTitle>
              <DialogDescription>
                {matchToStart && `${getTeamName(matchToStart.team1_id)} vs ${getTeamName(matchToStart.team2_id)}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-3">
              <Label>Select Umpire (optional)</Label>
              <Select value={selectedUmpireId} onValueChange={setSelectedUmpireId}>
                <SelectTrigger data-testid="start-match-umpire-select">
                  <SelectValue placeholder="Choose umpire — defaults to you" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Me ({user?.name || 'Organizer'})</SelectItem>
                  {umpires.map(u => (
                    <SelectItem key={u.umpire_id} value={u.umpire_id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {umpires.length === 0 && (
                <p className="text-xs text-muted-foreground">No umpires registered. Add them in the Umpires tab.</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStartMatchOpen(false)}>Cancel</Button>
              <Button onClick={confirmStartMatch} className="bg-secondary hover:bg-secondary/90" data-testid="confirm-start-match-btn">
                <Play className="mr-1 h-4 w-4" />Start
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Scoring Dialog */}
        <Dialog open={scoreDialogOpen} onOpenChange={(o) => { setScoreDialogOpen(o); if (!o) setTimeout(fetchData, 900); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Live Scoring</DialogTitle>
              <DialogDescription>
                {scoringMatch && (
                  <>
                    {scoringMatch.match_number ? `Match #${scoringMatch.match_number} · ` : ''}
                    {scoringMatch.pool_name ? `Pool ${scoringMatch.pool_name} · ` : scoringMatch.round ? `${scoringMatch.round} · ` : ''}
                    {getTeamName(scoringMatch.team1_id)} vs {getTeamName(scoringMatch.team2_id)}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            {scoringMatch && (
              <TournamentMatchScorer
                team1Name={getTeamName(scoringMatch.team1_id)}
                team2Name={getTeamName(scoringMatch.team2_id)}
                team1Id={scoringMatch.team1_id}
                team2Id={scoringMatch.team2_id}
                pointsPerGame={tournament?.points_per_game || 21}
                bestOf={tournament?.best_of || 1}
                initialGames={scoringMatch.scores || []}
                matchNumber={scoringMatch.match_number}
                poolLabel={scoringMatch.pool_name ? `Pool ${scoringMatch.pool_name}` : (scoringMatch.round || null)}
                onLiveSync={pushLiveScore}
                onSubmit={handleSubmitScore}
                onCancel={() => setScoreDialogOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Scoring PIN prompt — opens automatically on 403 PIN_REQUIRED */}
        <ScoringPinDialog
          open={pinDialogOpen}
          onOpenChange={(o) => {
            setPinDialogOpen(o);
            if (!o) setPendingScoringAction(null);
          }}
          tournamentId={tournamentId}
          onSubmit={handlePinSubmit}
        />

        {/* Walkover dialog — pick which team gets the win */}
        <Dialog open={!!walkoverMatch} onOpenChange={(o) => !o && setWalkoverMatch(null)}>
          <DialogContent className="sm:max-w-md" data-testid="walkover-dialog">
            <DialogHeader>
              <DialogTitle>Record Walkover</DialogTitle>
              <DialogDescription>
                {walkoverMatch?.match_number ? `Match #${walkoverMatch.match_number} — ` : ''}
                The match is recorded as 0-0 (Walkover) and the winning team still receives full points. Pick the team that gets the win.
              </DialogDescription>
            </DialogHeader>
            {walkoverMatch && (
              <div className="grid gap-2 py-2">
                <Button variant="outline" className="justify-start h-12" onClick={() => handleWalkover(walkoverMatch.team1_id)} data-testid="walkover-team1-btn">
                  <Trophy className="h-4 w-4 mr-2 text-accent" />{getTeamName(walkoverMatch.team1_id)} wins by walkover
                </Button>
                <Button variant="outline" className="justify-start h-12" onClick={() => handleWalkover(walkoverMatch.team2_id)} data-testid="walkover-team2-btn">
                  <Trophy className="h-4 w-4 mr-2 text-accent" />{getTeamName(walkoverMatch.team2_id)} wins by walkover
                </Button>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setWalkoverMatch(null)} data-testid="walkover-cancel-btn">Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit team count dialog — organizer-only, raises the max_participants soft cap */}
        <Dialog open={editCountOpen} onOpenChange={setEditCountOpen}>
          <DialogContent className="sm:max-w-md" data-testid="edit-team-count-dialog">
            <DialogHeader>
              <DialogTitle>Edit Team Count</DialogTitle>
              <DialogDescription>
                Set the maximum number of teams that can register for this tournament.
                Current registrations: <span className="font-bold">{tournament?.teams?.length || 0}</span>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label htmlFor="edit-count-input">Maximum Teams</Label>
              <Input
                id="edit-count-input"
                type="number"
                min="2"
                max="256"
                value={editCountValue}
                onChange={(e) => setEditCountValue(e.target.value)}
                data-testid="edit-team-count-input"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Tip: raise the cap before sharing the invite link to let more teams register.
                If pools are already generated, use <span className="font-semibold">Expand Bracket</span> afterwards to fold new teams in.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditCountOpen(false)} data-testid="cancel-edit-count-btn">Cancel</Button>
              <Button
                onClick={handleSaveCount}
                disabled={savingCount}
                className="bg-secondary hover:bg-secondary/90"
                data-testid="save-team-count-btn"
              >
                {savingCount ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Expand Bracket dialog — destructive vs additive strategy choice */}
        <Dialog open={expandDialogOpen} onOpenChange={setExpandDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="expand-bracket-dialog">
            <DialogHeader>
              <DialogTitle>Expand the bracket</DialogTitle>
              <DialogDescription>
                {orphanTeamIds.length} new {orphanTeamIds.length === 1 ? 'team' : 'teams'} pending placement. Choose how to absorb them.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              <label
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${expandStrategy === 'append' ? 'border-amber-400 bg-amber-500/5' : 'hover:border-muted-foreground'}`}
                data-testid="strategy-append"
              >
                <input
                  type="radio"
                  name="strategy"
                  value="append"
                  checked={expandStrategy === 'append'}
                  onChange={() => setExpandStrategy('append')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-semibold text-sm">Append <span className="text-xs text-emerald-500 font-normal">(Recommended)</span></div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Place new teams in the smallest existing pools. <strong>All completed matches preserved.</strong> Only new matches between the new teams and their pool mates are created.
                  </div>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${expandStrategy === 'reset_pools' ? 'border-destructive bg-destructive/5' : 'hover:border-muted-foreground'}`}
                data-testid="strategy-reset"
              >
                <input
                  type="radio"
                  name="strategy"
                  value="reset_pools"
                  checked={expandStrategy === 'reset_pools'}
                  onChange={() => setExpandStrategy('reset_pools')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-semibold text-sm">Reset all pool matches <span className="text-xs text-destructive font-normal">(Destructive)</span></div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Wipe every pool match (including completed ones) and regenerate the full round-robin from scratch with all teams. Use only if seeding fairness matters more than existing results.
                  </div>
                </div>
              </label>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setExpandDialogOpen(false)} disabled={expanding} data-testid="expand-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleExpandConfirm}
                disabled={expanding}
                className={expandStrategy === 'reset_pools' ? 'bg-destructive hover:bg-destructive/90' : ''}
                data-testid="expand-confirm"
              >
                {expanding ? 'Expanding…' : expandStrategy === 'reset_pools' ? 'Reset & Regenerate' : 'Expand'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

function MatchCard({ match, getTeamName, isOrganizer, onStart, onScore, onWalkover }) {
  const statusColors = { pending: 'bg-gray-500', live: 'bg-red-500 animate-pulse', completed: 'bg-green-500' };
  return (
    <Card className={cn('transition-all', match.status === 'live' && 'border-red-500/30')} data-testid={`match-card-${match.match_id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {match.match_number && (
                <span className="text-[10px] font-mono font-bold border border-border rounded px-1.5 py-0.5 text-muted-foreground" data-testid={`match-number-${match.match_id}`}>
                  #{match.match_number}
                </span>
              )}
              <Badge className={cn('text-[10px]', statusColors[match.status])}>{match.status}</Badge>
              {match.pool_name && <span className="text-xs text-muted-foreground">Pool {match.pool_name}</span>}
              {match.round && <span className="text-xs text-muted-foreground">{match.round}</span>}
              {match.walkover && (
                <Badge variant="outline" className="text-[10px] text-amber-500 border-amber-500/40" data-testid={`walkover-badge-${match.match_id}`}>Walkover</Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className={cn('font-bold', match.winner_id === match.team1_id && 'text-green-500')}>{getTeamName(match.team1_id)}</span>
              <span className="text-muted-foreground font-mono text-xs">vs</span>
              <span className={cn('font-bold', match.winner_id === match.team2_id && 'text-green-500')}>{getTeamName(match.team2_id)}</span>
            </div>
            {match.walkover ? (
              <div className="flex gap-2 mt-1">
                <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">0-0 (Walkover)</span>
              </div>
            ) : match.scores?.length > 0 && (
              <div className="flex gap-2 mt-1">
                {match.scores.map((g, i) => (
                  <span key={`${match.match_id}-g${i}`} className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">{g.team1_score}-{g.team2_score}</span>
                ))}
              </div>
            )}
          </div>
          {isOrganizer && (
            <div className="flex gap-1">
              {match.status === 'pending' && (
                <Button size="sm" variant="outline" onClick={() => onStart(match.match_id)} data-testid={`start-match-${match.match_id}`}>
                  <Play className="h-3.5 w-3.5 mr-1" />Start
                </Button>
              )}
              {match.status === 'live' && (
                <Button size="sm" onClick={() => onScore(match)} data-testid={`score-match-${match.match_id}`}>
                  <Swords className="h-3.5 w-3.5 mr-1" />Score
                </Button>
              )}
              {match.status !== 'completed' && onWalkover && (
                <Button size="sm" variant="ghost" onClick={() => onWalkover(match)} title="Record walkover (0-0, points to opponent)" data-testid={`walkover-match-${match.match_id}`}>
                  <Flag className="h-3.5 w-3.5 mr-1 text-amber-500" />W/O
                </Button>
              )}
            </div>
          )}
          {match.status === 'completed' && match.winner_id && (
            <div className="flex items-center gap-1 text-xs text-green-500">
              <CheckCircle className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{getTeamName(match.winner_id)}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
